
const isNonEmpty = (v) => {
  if (v === null || v === undefined) return false;
  return String(v).trim().length > 0;
};

const countNonEmptyFields = (obj) => {
  if (!obj || typeof obj !== 'object') return 0;
  return Object.values(obj).filter(isNonEmpty).length;
};

// Helper function to convert text to Title Case (first letter of each word uppercase)
const toTitleCase = (str) => {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const toKey = (apiKey) => {
  const map = {
    Fuel: 'fuel',
    Transmission: 'transmission',
    Odometer: 'odometer',
    Driveline: 'driveline',
    Engine: 'engine',
    CityMileage: 'cityMileage',
    HighwayMileage: 'highwayMileage',
    Style: 'style',
    FuelTank: 'fuelTank',
    NoOfCylinders: 'NoOfCylinders'
  };
  return map[apiKey] || apiKey;
};

const FIELD_CONFIG = {
  Fuel: { label: 'Fuel', iconType: 'fuel-pump' },
  Transmission: { label: 'Transmission', iconType: 'transmission' },
  Odometer: { label: 'Odometer', iconType: 'odometer' },
  Driveline: { label: 'Drive Type', iconType: 'driveline' },
  Engine: { label: 'Engine', iconType: 'engine' },
  CityMileage: { label: 'City Mileage', iconType: 'speedometer' },
  HighwayMileage: { label: 'Highway Mileage', iconType: 'speedometer' },
  Style: { label: 'Style', iconType: 'style' },
  FuelTank: { label: 'Fuel Tank', iconType: 'fuel-tank' },
  NoOfCylinders: { label: 'Cylinders', iconType: 'cylinders' }
};

export const HIGHLIGHT_PRIORITY_API_KEYS = [
  'Fuel',
  'Transmission',
  'Odometer',
  'Driveline',
  'Engine',
  'CityMileage',
  'HighwayMileage',
  'Style',
  'FuelTank',
  'NoOfCylinders'
];

export const VIN_MANDATORY_API_KEYS = ['Make', 'Model', 'Year', 'Trim'];

export const buildVehicleHighlightsFromVinResponse = (vinResponse) => {
  const data = vinResponse?.data;

  // Hard failure cases
  if (vinResponse?.error === true || !data) {
    return { enabled: false, vehicleData: null, specItemsConfig: [] };
  }

  // Validation 1: at least 10 non-empty fields
  const nonEmptyCount = countNonEmptyFields(data);
  if (nonEmptyCount < 10) {
    return { enabled: false, vehicleData: null, specItemsConfig: [] };
  }

  // Validation 2: mandatory fields present and non-empty
  const hasAllMandatory = VIN_MANDATORY_API_KEYS.every((k) => isNonEmpty(data?.[k]));
  if (!hasAllMandatory) {
    return { enabled: false, vehicleData: null, specItemsConfig: [] };
  }

  // Pick up to 6 highlight fields from priority list
  const selectedApiKeys = [];
  for (const apiKey of HIGHLIGHT_PRIORITY_API_KEYS) {
    if (selectedApiKeys.length >= 6) break;
    if (isNonEmpty(data?.[apiKey])) {
      selectedApiKeys.push(apiKey);
    }
  }

  if (selectedApiKeys.length === 0) {
    return { enabled: false, vehicleData: null, specItemsConfig: [] };
  }

  // Build vehicleData
  const vehicleData = {
    make: String(data.Make || '').trim(),
    modelName: String(data.Model || '').trim(),
    trim: String(data.Trim || '').trim(),
    year: String(data.Year || '').trim()
  };

  for (const apiKey of selectedApiKeys) {
    const key = toKey(apiKey);
    const cfg = FIELD_CONFIG[apiKey];
    let raw = String(data?.[apiKey]).trim();
    raw = toTitleCase(raw);

    const unitSuffix = cfg?.unitSuffix || '';
    vehicleData[key] = `${raw}${unitSuffix}`.trim();
  }

  // Build specItemsConfig with alternating icon positions
  const specItemsConfig = selectedApiKeys.map((apiKey, index) => {
    const key = toKey(apiKey);
    const cfg = FIELD_CONFIG[apiKey] || { label: apiKey, iconType: 'car-front' };
    return {
      key,
      label: cfg.label,
      iconType: cfg.iconType,
      iconPosition: index % 2 === 0 ? 'left' : 'right'
    };
  });

  return {
    enabled: true,
    vehicleData,
    specItemsConfig
  };
};
